import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, FileText, Target, Shield, ArrowRight } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { SectionHeading } from '../components/SectionHeading';
import { CTASection } from '../components/CTASection';
import { ServiceCard, FAQAccordion } from '../components/Cards';
import { faqs } from '../data/mockData';
import { PageHero } from '../components/PageHero';
export function Bedrijfswaardering() {
  return <div className="w-full" data-id="element-264">
      <PageHero label="Onze kernexpertise" title={<>
            Professionele <br data-id="element-266" />
            <span className="font-medium" data-id="element-267">Bedrijfswaardering</span>
          </>} subtitle="Een objectieve, onafhankelijke waardebepaling van uw onderneming. Krijg het strategische inzicht dat u nodig heeft voor een sterke onderhandelingspositie of juridische zekerheid." data-id="element-265">
        <div className="mt-10" data-id="element-268">
          <Link to="/contact" className="inline-block bg-accent hover:bg-accent-hover text-white px-8 py-4 rounded-full text-base font-medium transition-all shadow-subtle" data-id="element-269">
            Start waardebepaling
          </Link>
        </div>
      </PageHero>

      {/* Wat is het & Wat ontvang je */}
      <section className="py-24 bg-offwhite" data-id="element-270">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-271">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-272">
            <AnimatedSection data-id="element-273">
              <SectionHeading title="Wat is een bedrijfswaardering?" className="mb-6" data-id="element-274" />
              <div className="prose prose-lg text-charcoal/70" data-id="element-275">
                <p className="mb-4" data-id="element-276">
                  Een bedrijfswaardering is een objectieve berekening van de
                  economische waarde van een onderneming op een specifiek
                  moment. Het is geen simpele rekensom van bezittingen minus
                  schulden, maar een complexe analyse van toekomstige
                  kasstromen, risicoprofielen en marktomstandigheden.
                </p>
                <p className="mb-4" data-id="element-277">
                  De timing van een waardering is cruciaal. De waarde van uw
                  bedrijf is afhankelijk van macro-economische factoren,
                  sectortrends en de interne organisatie. Bovendien is er een
                  belangrijk verschil tussen <em data-id="element-278">waarde</em> (een objectieve
                  berekening) en <em data-id="element-279">prijs</em> (wat er uiteindelijk na
                  onderhandeling betaald wordt).
                </p>
                <p className="mb-6" data-id="element-280">
                  Wij hanteren academisch onderbouwde methoden, zoals de
                  Discounted Cash Flow (DCF) methode, gecombineerd met
                  praktische marktkennis. Lees meer over de{' '}
                  <Link to="/kennisbank/methodes/ebitda-multiple-uitleg" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-281">
                    EBITDA multiple
                  </Link>
                  . Dit resulteert in een robuust rapport dat standhoudt in
                  onderhandelingen en voor de rechtbank.
                </p>
                <Link to="/kennisbank/bedrijfswaardering/wat-is-een-bedrijfswaardering" className="text-accent font-medium hover:underline flex items-center gap-2" data-id="element-282">
                  Lees meer in onze kennisbank <ArrowRight size={16} data-id="element-283" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2} data-id="element-284">
              <div className="bg-white p-10 rounded-2xl border border-warmgray/50 shadow-sm" data-id="element-285">
                <h3 className="text-2xl font-light mb-6" data-id="element-286">Wat u ontvangt</h3>
                <ul className="space-y-4" data-id="element-287">
                  <li className="flex items-start gap-4" data-id="element-288">
                    <FileText className="text-accent shrink-0 mt-1" size={24} data-id="element-289" />
                    <div data-id="element-290">
                      <h4 className="font-medium text-charcoal" data-id="element-291">
                        Uitgebreid Waarderingsrapport
                      </h4>
                      <p className="text-sm text-charcoal/70 mt-1" data-id="element-292">
                        Een helder, leesbaar document met volledige onderbouwing
                        van de waarde.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-293">
                    <Target className="text-accent shrink-0 mt-1" size={24} data-id="element-294" />
                    <div data-id="element-295">
                      <h4 className="font-medium text-charcoal" data-id="element-296">
                        Strategisch Inzicht
                      </h4>
                      <p className="text-sm text-charcoal/70 mt-1" data-id="element-297">
                        Inzicht in de belangrijkste waardedrivers en risico's
                        van uw onderneming.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-298">
                    <Shield className="text-accent shrink-0 mt-1" size={24} data-id="element-299" />
                    <div data-id="element-300">
                      <h4 className="font-medium text-charcoal" data-id="element-301">
                        Onafhankelijk Oordeel
                      </h4>
                      <p className="text-sm text-charcoal/70 mt-1" data-id="element-302">
                        Een objectief standpunt dat u kunt gebruiken richting
                        kopers, fiscus of rechtbank.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Hoe wij waarderen */}
      <section className="py-24 bg-white border-b border-warmgray/50" data-id="element-303">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-304">
          <AnimatedSection className="max-w-3xl mx-auto text-center" data-id="element-305">
            <SectionHeading title="Hoe wij waarderen" align="center" className="mx-auto mb-8" data-id="element-306" />
            <div className="prose prose-lg text-charcoal/70 mx-auto" data-id="element-307">
              <p className="mb-4 text-left" data-id="element-308">
                Een gedegen waardering begint met het begrijpen van uw
                bedrijfsmodel. Wij analyseren de historische cijfers en voeren
                een <strong data-id="element-309">normalisatie</strong> uit om incidentele baten en
                lasten te corrigeren. Dit geeft inzicht in de structurele
                verdiencapaciteit.
              </p>
              <p className="mb-10 text-left" data-id="element-310">
                Vervolgens passen we de juiste waarderingsmethoden toe. Meestal
                is dit de Discounted Cash Flow (DCF) methode, waarbij we kijken
                naar de contante waarde van toekomstige kasstromen. Ter controle
                gebruiken we vaak marktmultiples (zoals EBITDA multiples) om de
                uitkomsten te valideren.
              </p>
              <Link to="/werkwijze" className="inline-block bg-offwhite border border-warmgray/50 hover:border-accent text-charcoal px-6 py-3 rounded-full text-sm font-medium transition-all shadow-sm" data-id="element-311">
                Bekijk ons volledige stappenplan
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Situaties */}
      <section className="py-24 bg-offwhite" data-id="element-312">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-313">
          <AnimatedSection className="text-center mb-16" data-id="element-314">
            <SectionHeading title="Kies uw specifieke situatie" subtitle="Wanneer nodig" align="center" className="mx-auto" data-id="element-315" />
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-316">
            <AnimatedSection delay={0.1} data-id="element-317">
              <ServiceCard title="Verkoop van uw bedrijf" description="Bepaal de juiste vraagprijs, identificeer waardedrivers en sta sterker in de onderhandelingen met potentiële kopers." link="/bedrijfswaardering-verkoop" data-id="element-318" />
            </AnimatedSection>
            <AnimatedSection delay={0.2} data-id="element-319">
              <ServiceCard title="Overname (Aankoop)" description="Krijg zekerheid over de waarde van het targetbedrijf en voorkom dat u te veel betaalt voor een overname." link="/bedrijfswaardering-overname" data-id="element-320" />
            </AnimatedSection>
            <AnimatedSection delay={0.3} data-id="element-321">
              <ServiceCard title="Echtscheiding" description="Een onafhankelijke waardering voorkomt discussie over de verdeling van de waarde van de onderneming." link="/bedrijfswaardering-scheiding" data-id="element-322" />
            </AnimatedSection>
            <AnimatedSection delay={0.4} data-id="element-323">
              <ServiceCard title="Aandeelhoudersconflict" description="Objectieve waardebepaling bij uittreding van een aandeelhouder om juridische escalatie te voorkomen." link="/bedrijfswaardering-conflict" data-id="element-324" />
            </AnimatedSection>
            <AnimatedSection delay={0.5} data-id="element-325">
              <ServiceCard title="Interne Overdracht" description="Fiscaal geaccepteerde waardering voor overdracht aan familieleden (bedrijfsopvolging) of management (MBO)." link="/bedrijfswaardering-interne-overdracht" data-id="element-326" />
            </AnimatedSection>
            <AnimatedSection delay={0.6} data-id="element-327">
              <div className="bg-charcoal p-8 rounded-2xl h-full flex flex-col justify-center items-center text-center" data-id="element-328">
                <h3 className="text-xl font-medium mb-3 text-white" data-id="element-329">
                  Andere situatie?
                </h3>
                <p className="text-warmgray/70 text-sm mb-6" data-id="element-330">
                  Wij helpen ook bij fiscale herstructureringen, fusies en
                  economische schade.
                </p>
                <Link to="/contact" className="text-accent hover:text-white transition-colors font-medium text-sm flex items-center" data-id="element-331">
                  Neem contact op <ArrowRight size={16} className="ml-2" data-id="element-332" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-20 bg-charcoal text-center" data-id="element-333">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-334">
          <AnimatedSection data-id="element-335">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6" data-id="element-336">
              Wilt u weten wat uw bedrijf waard is?
            </h2>
            <p className="text-warmgray/80 mb-8 max-w-2xl mx-auto text-lg" data-id="element-337">
              Krijg helderheid en strategisch inzicht met een onafhankelijk
              waarderingsrapport.
            </p>
            <Link to="/contact" className="inline-block bg-accent hover:bg-accent-hover text-white px-8 py-4 rounded-full text-base font-medium transition-all shadow-subtle" data-id="element-338">
              Neem direct contact op
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 bg-white" data-id="element-339">
        <div className="max-w-[800px] mx-auto px-6 lg:px-12" data-id="element-340">
          <AnimatedSection className="text-center mb-12" data-id="element-341">
            <SectionHeading title="Veelgestelde vragen" align="center" className="mx-auto" data-id="element-342" />
          </AnimatedSection>
          <AnimatedSection delay={0.2} className="bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-warmgray/50" data-id="element-343">
            {faqs.map((faq, index) => <FAQAccordion key={index} question={faq.question} answer={faq.answer} data-id="element-344" />)}
          </AnimatedSection>
        </div>
      </section>

      <div id="aanvragen" data-id="element-345">
        <CTASection title="Klaar om de waarde van uw bedrijf te ontdekken?" subtitle="Laat uw gegevens achter en wij nemen binnen 24 uur contact met u op voor een vrijblijvende intake." buttonText="Vraag offerte aan" buttonLink="/contact" data-id="element-346" />
      </div>
    </div>;
}